import React, { useState, useEffect, useCallback } from 'react';
import { softphoneApi } from '../../api/client';

/**
 * CallHistoryPanel — a sidecar showing the CURRENT agent's own recent calls
 * (this shift), newest-first. Mirrors QosPanel's layout/behaviour (collapse
 * strip + header + close). Purpose (Helen 2026-08-14): when a customer hangs
 * up after the CCR brings 911 on, the CCR still needs the caller ID to give to
 * 911 — but the live call display is gone once the call ends. This panel keeps
 * every call's caller ID visible for the shift, with one-click copy.
 *
 * Data: GET /softphone/call-history (scoped server-side to the agent's own
 * extension). Refreshes on open, on manual refresh, and whenever `refreshKey`
 * changes (parent bumps it when a call ends).
 */

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // Render in the AGENT'S OWN computer timezone (the backend now sends a
    // timezone-aware UTC timestamp), with the tz abbreviation so it's explicit
    // (e.g. "9:58 PM CDT"). Show the date too if the call isn't from today.
    const now = new Date();
    const sameDay = d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
    const time = d.toLocaleTimeString([], {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
    if (sameDay) return time;
    const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${date}, ${time}`;
  } catch {
    return iso;
  }
}

function DirBadge({ direction }) {
  const out = direction === 'outbound';
  return (
    <span style={{
      padding: '1px 5px', borderRadius: 999, fontSize: 9, fontWeight: 700,
      background: out ? '#EEF2FF' : '#ECFDF5',
      color: out ? '#4338CA' : '#047857',
      border: `1px solid ${out ? '#C7D2FE' : '#A7F3D0'}`,
      whiteSpace: 'nowrap',
    }}>
      {out ? '↗ OUT' : '↘ IN'}
    </span>
  );
}

function CallRow({ call }) {
  const [copied, setCopied] = useState(false);
  const [iidCopied, setIidCopied] = useState(false);
  const cid = call.caller_id || '—';

  const copy = useCallback(() => {
    if (!call.caller_id) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(call.caller_id).then(done).catch(() => {
          try {
            const ta = document.createElement('textarea');
            ta.value = call.caller_id; document.body.appendChild(ta); ta.select();
            document.execCommand('copy'); document.body.removeChild(ta); done();
          } catch { /* noop */ }
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = call.caller_id; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta); done();
      }
    } catch { /* noop */ }
  }, [call.caller_id]);

  return (
    <div style={{
      padding: '7px 4px',
      borderBottom: '1px dashed var(--cx-border, #e4e7eb)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <DirBadge direction={call.direction} />
        <span style={{ fontSize: 10, color: 'var(--cx-muted, #6B7280)' }}>
          {fmtTime(call.started_at)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 3 }}>
        <span style={{
          fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
          color: 'var(--cx-text, #1a202c)', wordBreak: 'break-all',
        }}>
          {cid}
        </span>
        {call.caller_id && (
          <button
            onClick={copy}
            title="Copy caller ID"
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)',
              background: copied ? '#3BD162' : 'transparent',
              color: copied ? '#fff' : 'var(--cx-text, #1a202c)',
              padding: '2px 6px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {copied ? '✓ Copied' : '⧉ Copy'}
          </button>
        )}
      </div>
      {call.queue_name && (
        <div style={{ fontSize: 10, color: 'var(--cx-muted, #6B7280)', marginTop: 2 }}>
          {call.queue_name}
        </div>
      )}
      {call.cx_interaction_id && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <span style={{ fontSize: 9, color: 'var(--cx-muted, #6B7280)', textTransform: 'uppercase', letterSpacing: 0.4 }}>ID</span>
          <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--cx-muted, #6B7280)', wordBreak: 'break-all', flex: 1 }}>
            {call.cx_interaction_id}
          </span>
          <button
            onClick={() => { try { navigator.clipboard.writeText(call.cx_interaction_id); setIidCopied(true); setTimeout(() => setIidCopied(false), 1200); } catch (_) {} }}
            title="Copy Interaction ID"
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)',
              background: iidCopied ? '#3BD162' : 'transparent',
              color: iidCopied ? '#fff' : 'var(--cx-muted, #6B7280)',
              padding: '1px 5px', fontSize: 9, borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {iidCopied ? '✓' : '⧉'}
          </button>
        </div>
      )}
    </div>
  );
}

const AUTO_REFRESH_MS = 15000;

export default function CallHistoryPanel({ collapsed, onToggleCollapse, onClose, refreshKey }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem('cx-history-autorefresh') !== '0'; } catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem('cx-history-autorefresh', autoRefresh ? '1' : '0'); } catch {}
  }, [autoRefresh]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    try {
      const res = await softphoneApi.callHistory();
      setCalls(Array.isArray(res?.calls) ? res.calls : []);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount / expand, and whenever the parent bumps refreshKey (call ended).
  useEffect(() => {
    if (!collapsed) load();
  }, [collapsed, refreshKey, load]);

  // When a call ends the CDR row lands within a few seconds, but our parent
  // may bump refreshKey the instant the call goes IDLE (before the row is
  // written). Do a couple of short delayed re-fetches after each refreshKey
  // bump so the just-ended call reliably appears without the user waiting.
  useEffect(() => {
    if (collapsed || !refreshKey) return undefined;
    const t1 = setTimeout(load, 3000);
    const t2 = setTimeout(load, 8000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [refreshKey, collapsed, load]);

  // Optional auto-refresh: poll periodically while the panel is open.
  useEffect(() => {
    if (collapsed || !autoRefresh) return undefined;
    const iv = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(iv);
  }, [collapsed, autoRefresh, load]);

  // Collapsed strip — vertical "History" badge.
  if (collapsed) {
    return (
      <div
        title="Click to expand Call History"
        onClick={onToggleCollapse}
        style={{
          width: 36, minWidth: 36, maxWidth: 36, alignSelf: 'stretch',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'flex-start', paddingTop: 12, gap: 8,
          background: 'var(--cx-panel, #fff)',
          border: '1px solid var(--cx-border, #e4e7eb)',
          borderRadius: 8, cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
        }}
      >
        <div style={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontSize: 11, fontWeight: 600, letterSpacing: 1,
          color: 'var(--cx-text, #1a202c)',
        }}>
          ▸ History
        </div>
        <div style={{ fontSize: 13 }}>📞</div>
      </div>
    );
  }

  return (
    <div style={{
      flex: '0 0 260px', width: 260, maxWidth: 260, alignSelf: 'flex-start',
      maxHeight: 'calc(100vh - 24px)', display: 'flex', flexDirection: 'column',
      background: 'var(--cx-panel, #fff)',
      border: '1px solid var(--cx-border, #e4e7eb)',
      borderRadius: 8, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 10px', borderBottom: '1px solid var(--cx-border, #e4e7eb)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--cx-subtle, #f8fafc)',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>📞 Call History</span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            title={autoRefresh ? 'Auto-refresh ON (every 15s) — click to turn off' : 'Auto-refresh OFF — click to turn on'}
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)',
              background: autoRefresh ? '#3BD162' : 'transparent',
              color: autoRefresh ? '#fff' : 'var(--cx-muted, #6B7280)',
              padding: '2px 6px', fontSize: 10, fontWeight: 600, borderRadius: 4, cursor: 'pointer',
            }}
          >
            {autoRefresh ? '⟳ Auto' : '⟳ Auto'}
          </button>
          <button
            onClick={load}
            title="Refresh now"
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)', background: 'transparent',
              padding: '2px 6px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
              color: 'var(--cx-text, #1a202c)',
            }}
          >
            ⟳
          </button>
          <button
            onClick={onToggleCollapse}
            title="Collapse"
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)', background: 'transparent',
              padding: '2px 6px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
              color: 'var(--cx-text, #1a202c)',
            }}
          >
            ◂
          </button>
          {onClose && (
            <button
              onClick={onClose}
              title="Close"
              style={{
                border: '1px solid var(--cx-border, #e4e7eb)', background: 'transparent',
                padding: '2px 6px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                color: 'var(--cx-text, #1a202c)',
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '4px 10px', overflowY: 'auto' }}>
        {loading && calls.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--cx-muted, #6B7280)', padding: '12px 4px', textAlign: 'center' }}>
            Loading…
          </div>
        ) : err ? (
          <div style={{ fontSize: 11, color: '#E53935', padding: '12px 4px', textAlign: 'center' }}>
            Couldn’t load history.<br/>
            <button onClick={load} style={{ marginTop: 6, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : calls.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--cx-muted, #6B7280)', padding: '12px 4px', textAlign: 'center' }}>
            No calls this shift yet.
          </div>
        ) : (
          calls.map((c) => <CallRow key={c.id} call={c} />)
        )}
        <div style={{ marginTop: 8, marginBottom: 4, fontSize: 10, color: 'var(--cx-muted, #6B7280)', fontStyle: 'italic' }}>
          Your inbound calls this shift (last 12h){autoRefresh ? ' · auto-refresh on' : ''}. Tap ⧉ to copy a caller ID.
        </div>
      </div>
    </div>
  );
}
